/**
 * Utility functions for match-day presence and timing logic
 */

/**
 * Checks if the current time is within the allowed match window.
 * Logical window: 3 hours before kickoff to 4 hours after kickoff.
 * 
 * @param kickoffDate The kickoff datetime
 * @param now The current datetime (optional, for testing)
 * @returns boolean
 */
export const isWithinMatchWindow = (kickoffDate: Date, now: Date = new Date()): boolean => {
    const kickoff = kickoffDate.getTime();
    const currentTime = now.getTime();
    const windowStart = kickoff - 3 * 60 * 60 * 1000;
    const windowEnd = kickoff + 4 * 60 * 60 * 1000;

    return currentTime >= windowStart && currentTime <= windowEnd;
};

/**
 * Checks if the survey feature should be closed.
 * Rule: 30 minutes after the match has officially finished.
 */
export const isSurveyClosed = (matchFinishedAt: Date | null, now: Date = new Date()): boolean => {
    if (!matchFinishedAt) return false;

    const thirtyMinutesAfterFinish = matchFinishedAt.getTime() + 30 * 60 * 1000;
    return now.getTime() > thirtyMinutesAfterFinish;
};

/**
 * Presence Cookie Helpers
 * Used to ensure the user has physically scanned a tag recently.
 */
export const PRESENCE_COOKIE_NAME = 'idseat_presence';
export const PRESENCE_COOKIE_EXPIRY_HOURS = 4;

export const setPresenceCookie = () => {
    const d = new Date();
    d.setTime(d.getTime() + (PRESENCE_COOKIE_EXPIRY_HOURS * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    // Path=/ ensures it works across all fan subpages
    // SameSite=Lax is standard for this kind of token
    document.cookie = `${PRESENCE_COOKIE_NAME}=true;${expires};path=/;SameSite=Lax`;
};

export const hasPresenceCookie = (): boolean => {
    const name = PRESENCE_COOKIE_NAME + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return true;
        }
    }
    return false;
};
